import React, { useState, useEffect } from 'react';
import { format, subDays, addDays } from 'date-fns';
import { Container, Row, Col, Form, Table, Spinner, Alert, Button } from 'react-bootstrap';
import * as XLSX from 'xlsx';

export const route = "/mimo/register-summary";
export const role = "MIMO";

const RegisterSummary = () => {
    const today = format(new Date(), 'yyyy-MM-dd');
    const [startDate, setStartDate] = useState(today);
    const [endDate, setEndDate] = useState(today);
    const [registerId, setRegisterId] = useState('');
    const [registerSummaries, setRegisterSummaries] = useState([]);
    const [registers, setRegisters] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    const fetchRegisters = async () => {
        try {
            const response = await fetch(`${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/registers/`, {
                headers: {
                    'ngrok-skip-browser-warning': 'true'
                }
            });

            if (!response.ok) {
                throw new Error('Network response was not ok');
            }

            const data = await response.json();
            setRegisters(data);
        } catch {
            setError('Failed to fetch registers');
        }
    };

    useEffect(() => {
        fetchRegisters();
    }, []);

    useEffect(() => {
        const fetchRegisterSummary = async () => {
            setLoading(true);
            setError(null);
            try {
                const adjustedStartDate = startDate;
                const adjustedEndDate = endDate;

                const response = await fetch(`${process.env.REACT_APP_SNOWFLAKE_URL}/mimo/register-summary/${adjustedStartDate}/${adjustedEndDate}/?registerId=${registerId}`, {
                    headers: {
                        'ngrok-skip-browser-warning': 'true' 
                    }
                });

                if (!response.ok) {
                    throw new Error('Network response was not ok');
                }

                const data = await response.json();
                setRegisterSummaries(data.sort((a, b) => a.REGISTER_ID - b.REGISTER_ID));
            } catch {
                setError('Failed to fetch register summary data');
            } finally {
                setLoading(false);
            }
        };

        fetchRegisterSummary();
    }, [startDate, endDate, registerId]);

    const formatCurrency = (value) => {
        const num = Number(value) || 0;
        const abs = Math.abs(num).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 });
        return num < 0 ? `($${abs})` : `$${abs}`;
    };

    const totals = registerSummaries.reduce(
        (acc, row) => ({
            DISPENSE: acc.DISPENSE + (Number(row.DISPENSE) || 0),
            DEPOSIT: acc.DEPOSIT + (Number(row.DEPOSIT) || 0),
            TOTAL: acc.TOTAL + (Number(row.TOTAL) || 0),
            ADJUSTMENT: acc.ADJUSTMENT + (Number(row.ADJUSTMENT) || 0),
            ADJUSTED_TOTAL: acc.ADJUSTED_TOTAL + (Number(row.ADJUSTED_TOTAL) || 0),
        }),
        { DISPENSE: 0, DEPOSIT: 0, TOTAL: 0, ADJUSTMENT: 0, ADJUSTED_TOTAL: 0 }
    );

    const exportToExcel = () => {
        const rows = registerSummaries.map(row => ({
            Date: row.DATE,
            'Register ID': row.REGISTER_ID,
            'Register Name': row.REGISTER_NAME,
            Dispense: formatCurrency(row.DISPENSE),
            Deposit: formatCurrency(row.DEPOSIT),
            Total: formatCurrency(row.TOTAL),
            Adjustment: formatCurrency(row.ADJUSTMENT),
            'Adjusted Total': formatCurrency(row.ADJUSTED_TOTAL),
        }));
        rows.push({
            Date: '',
            'Register ID': '',
            'Register Name': 'Grand Total',
            Dispense: formatCurrency(totals.DISPENSE),
            Deposit: formatCurrency(totals.DEPOSIT),
            Total: formatCurrency(totals.TOTAL),
            Adjustment: formatCurrency(totals.ADJUSTMENT),
            'Adjusted Total': formatCurrency(totals.ADJUSTED_TOTAL),
        });
        const ws = XLSX.utils.json_to_sheet(rows);
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, 'Register Summary');
        const fileName = `Register_Summary_${startDate}_to_${endDate}.xlsx`;
        XLSX.writeFile(wb, fileName);
    };

    return (
        <Container>
            <h1 className="mt-4">Register Summary</h1>
            <Form>
                <Row className="mb-3">
                    <Col>
                        <Form.Group controlId="startDate">
                            <Form.Label>Start Date:</Form.Label>
                            <Form.Control
                                type="date"
                                value={startDate}
                                onChange={(e) => setStartDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId="endDate">
                            <Form.Label>End Date:</Form.Label>
                            <Form.Control
                                type="date"
                                value={endDate}
                                onChange={(e) => setEndDate(e.target.value)}
                            />
                        </Form.Group>
                    </Col>
                    <Col>
                        <Form.Group controlId="registerId">
                            <Form.Label>Register ID:</Form.Label>
                            <Form.Control
                                as="select"
                                value={registerId}
                                onChange={(e) => setRegisterId(e.target.value)}
                            >
                                <option value="">All Registers</option>
                                {registers.map(register => (
                                    <option key={register.REGISTER_ID} value={register.REGISTER_ID}>{register.REGISTER_NAME}</option>
                                ))}
                            </Form.Control>
                        </Form.Group>
                    </Col>
                </Row>
            </Form>
            {loading ? (
                <Spinner animation="border" variant="primary" />
            ) : error ? (
                <Alert variant="danger">{error}</Alert>
            ) : registerSummaries.length === 0 ? (
                <Alert variant="info">No data found for the selected dates.</Alert>
            ) : (
                <>
                    <Button variant="success" onClick={exportToExcel} className="mb-3">
                        Export to Excel
                    </Button>
                    <Table striped bordered hover className="mt-4 mb-3">
                        <thead>
                            <tr>
                                <th>Date</th>
                                <th>Register ID</th>
                                <th>Register Name</th>
                                <th>Dispense</th>
                                <th>Deposit</th>
                                <th>Total</th>
                                <th>Adjustment</th>
                                <th>Adjusted Total</th>
                            </tr>
                        </thead>
                        <tbody>
                            {registerSummaries.map((summary, index) => (
                                <tr key={index}>
                                    <td>{summary.DATE}</td>
                                    <td>{summary.REGISTER_ID}</td>
                                    <td>{summary.REGISTER_NAME}</td>
                                    <td>{formatCurrency(summary.DISPENSE)}</td>
                                    <td>{formatCurrency(summary.DEPOSIT)}</td>
                                    <td>{formatCurrency(summary.TOTAL)}</td>
                                    <td>{formatCurrency(summary.ADJUSTMENT)}</td>
                                    <td>{formatCurrency(summary.ADJUSTED_TOTAL)}</td>
                                </tr>
                            ))}
                        </tbody>
                        <tfoot>
                            <tr style={{ fontWeight: 'bold' }}>
                                <td></td>
                                <td></td>
                                <td>Grand Total</td>
                                <td>{formatCurrency(totals.DISPENSE)}</td>
                                <td>{formatCurrency(totals.DEPOSIT)}</td>
                                <td>{formatCurrency(totals.TOTAL)}</td>
                                <td>{formatCurrency(totals.ADJUSTMENT)}</td>
                                <td>{formatCurrency(totals.ADJUSTED_TOTAL)}</td>
                            </tr>
                        </tfoot>
                    </Table>
                </>
            )}
        </Container>
    );
};

export default RegisterSummary;